import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Button, TextField, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  TablePagination, Chip, Tooltip, Typography, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import { usePermissions } from '../../../utils/usePermissions';
import { ReportSecurityWrapper } from './ReportSecurityWrapper';
import { notify } from '../../notifications';

interface User {
  id: number;
  name: string;
}

interface StoredData {
  users: User[];
  timestamp: number;
  date: string;
  checkType: 'missing' | 'filled';
  scope: string;
}

const CACHE_KEY = 'userCheckData';
const CACHE_TTL_MS = 600000;

// «Вчера» считается в том же часовом поясе, что и на сервере (reportsV2.js), а не по часам браузера
const reportDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Almaty',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

function getYesterdaysDate(): string {
  const [y, m, d] = reportDateFormatter.format(new Date()).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

function readCache(): StoredData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredData;
    return Array.isArray(data?.users) ? data : null;
  } catch {
    return null;
  }
}

const CheckUsersPerDay: React.FC = () => {
  const { hasPerm, getScope } = usePermissions();

  const fillScope = getScope('reports.fill_monitoring');
  const dateLimit = getScope('reports.fill_monitoring.date_limit');
  // Свободный выбор даты только при явном 'any' (или ранге 100, getScope отдаёт 'all'); иначе - только вчера.
  // Совпадает с серверной проверкой: 'none' и отсутствие права не открывают произвольные даты
  const isYesterdayOnly = dateLimit !== 'any' && dateLimit !== 'all';
  const canProtectCopy = hasPerm('reports.fill_monitoring.protect_copy');
  const yesterdayStr = useMemo(() => getYesterdaysDate(), []);

  const [missingUsers, setMissingUsers] = useState<User[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(yesterdayStr);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkType, setCheckType] = useState<'missing' | 'filled'>('missing');

  // При ограничении роли фиксируем дату на вчерашнем дне
  useEffect(() => {
    if (isYesterdayOnly) {
      setSelectedDate(yesterdayStr);
    }
  }, [isYesterdayOnly, yesterdayStr]);

  // Восстановление результата предыдущей проверки: только свежий кэш, по той же области видимости
  // и (при ограничении «только вчера») строго за вчерашний день
  useEffect(() => {
    const stored = readCache();
    if (!stored) return;
    if (Date.now() - stored.timestamp >= CACHE_TTL_MS) return;
    if (stored.scope !== fillScope) return;
    if (isYesterdayOnly && stored.date !== yesterdayStr) return;

    setMissingUsers(stored.users);
    setCheckType(stored.checkType);
    if (!isYesterdayOnly) {
      setSelectedDate(stored.date);
    }
    notify.info('Данные загружены из предыдущего сеанса');
  }, [isYesterdayOnly, fillScope, yesterdayStr]);

  useEffect(() => {
    document.title = 'Мониторинг сдачи отчетов | Zerde';
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, missingUsers]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return missingUsers;
    return missingUsers.filter(user => user.name.toLowerCase().includes(term));
  }, [searchTerm, missingUsers]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isYesterdayOnly) return;
    setSelectedDate(e.target.value);
  };

  const handleCheck = async (type: 'missing' | 'filled') => {
    const targetDate = isYesterdayOnly ? yesterdayStr : selectedDate;
    if (!targetDate) {
      notify.error('Не выбрана дата!');
      return;
    }

    setCheckType(type);
    setLoading(true);
    try {
      const response = await axios.get('/api/v2/reports/check-users', {
        params: { 
          date: targetDate,
          checkType: type
        }
      });
      const usersWithIds = response.data.users.map((name: string, index: number) => ({ id: index + 1, name }));
      
      const storageData: StoredData = {
        users: usersWithIds,
        timestamp: Date.now(),
        date: targetDate,
        checkType: type,
        scope: fillScope
      };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(storageData));
      } catch {
        // хранилище недоступно или переполнено - кэш просто не сохраняется
      }
      
      setMissingUsers(usersWithIds);
      notify.success(`Найдено ${usersWithIds.length} аптек (${type === 'missing' ? 'не заполнили' : 'заполнили'}).`);
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Ошибка при проверке сдачи отчетов';
      notify.error(msg);
      console.error('Ошибка при проверке пользователей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!hasPerm('reports.fill_monitoring')) {
    return null;
  }

  const getScopeLabel = () => {
    switch (fillScope) {
      case 'own_region': return { label: 'Свой регион', color: '#06b6d4', bg: '#ecfeff' };
      case 'assigned_pharmacies': return { label: 'Курируемые аптеки', color: '#3b82f6', bg: '#eff6ff' };
      case 'all': return { label: 'Вся сеть аптек', color: '#10b981', bg: '#ecfdf5' };
      default: return null;
    }
  };

  const scopeInfo = getScopeLabel();

  return (
    <ReportSecurityWrapper
      protectCopy={canProtectCopy}
    >
      <Box>
      {/* Верхняя панель: фильтры и область видимости */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <Tooltip 
          title={isYesterdayOnly ? "Политика роли: доступен выбор только вчерашнего дня (текущий день - 1)" : ""}
          arrow
        >
          <TextField
            size="small"
            label="Дата отчета"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={isYesterdayOnly ? yesterdayStr : selectedDate}
            onChange={handleDateChange}
            disabled={isYesterdayOnly || loading}
            InputProps={{
              endAdornment: isYesterdayOnly ? (
                <InputAdornment position="end">
                  <LockIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                </InputAdornment>
              ) : undefined
            }}
            sx={{ minWidth: 170 }}
          />
        </Tooltip>

        <Button 
          variant={checkType === 'missing' ? "contained" : "outlined"}
          color="error" 
          onClick={() => handleCheck('missing')}
          disabled={loading}
          sx={{ minWidth: 150, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
        >
          {loading && checkType === 'missing' ? 'Загрузка...' : 'Не Заполнено'}
        </Button>

        <Button 
          variant={checkType === 'filled' ? "contained" : "outlined"}
          color="success" 
          onClick={() => handleCheck('filled')}
          disabled={loading}
          sx={{ minWidth: 150, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
        >
          {loading && checkType === 'filled' ? 'Загрузка...' : 'Заполнено'}
        </Button>

        <TextField
          size="small"
          placeholder="Поиск по номеру аптеки / имени..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 260, flexGrow: { xs: 1, md: 0 } }}
        />

        <Box sx={{ flexGrow: 1 }} />

        {/* Бейджи скоупа */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isYesterdayOnly && (
            <Chip
              icon={<CalendarMonthIcon sx={{ fontSize: '13px !important' }} />}
              label="Только вчера"
              size="small"
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#b45309',
                backgroundColor: '#fef3c7',
                border: '1px solid #fde68a'
              }}
            />
          )}
          {scopeInfo && (
            <Chip
              label={`Область: ${scopeInfo.label}`}
              size="small"
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: scopeInfo.color,
                backgroundColor: scopeInfo.bg,
                border: `1px solid ${scopeInfo.color}30`
              }}
            />
          )}
        </Box>
      </Box>

      {/* Список аптек */}
      {filteredUsers.length > 0 ? (
        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 80, color: '#475569' }}>№</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Аптека / Пользователь</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user, idx) => (
                    <TableRow 
                      key={user.id} 
                      hover
                      sx={{ '&:nth-of-type(even)': { backgroundColor: '#fcfcfd' } }}
                    >
                      <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {page * rowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.88rem' }}>
                        {user.name}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Строк на странице:"
          />
        </Paper>
      ) : missingUsers.length > 0 && searchTerm ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            По запросу «{searchTerm}» ничего не найдено
          </Typography>
        </Paper>
      ) : null}
    </Box>
    </ReportSecurityWrapper>
  );
};

export default CheckUsersPerDay;